<template>
  <div class="flex gap-2 align-items-start">
    <FindUser
      ref="findUserRef"
      :idInforme="props.idInforme"
      @add-user="handleAddUser"
      @search-users="handleSearchUsers"
      :canManageUsers="canManageUsers"
      class="flex-1"
    />
    <div v-if="canManageUsers">
      <Button
        icon="pi pi-ellipsis-v"
        severity="secondary"
        @click="toggleMenu"
        aria-haspopup="true"
        aria-controls="users-menu"
        v-tooltip.top="'Más opciones'"
      />
      <Menu
        ref="menu"
        id="users-menu"
        :model="menuItems"
        :popup="true"
      />
    </div>
  </div>

  <div class="overflow-x-auto mt-3">
    <DataTable
      :value="usersArray"
      :rowClass="rowClass"
      removableSort
      :loading="loading"
      loading-icon="pi pi-spin pi-spinner"
      scrollable
      :scrollHeight="totalHeight"
    >
      <template #empty>
        <div class="p-text-center">
          <span class="p-text-bold">No hay usuarios</span>
        </div>
      </template>
      <Column field="email">
        <template #header>
          <div
            @click="sortBy('email')"
            class="flex gap-2 align-items-center cursor-pointer"
          >
            <div>Usuario</div>
            <div v-if="sortField != 'email'" class="pi pi-sort-alt"></div>
            <div
              v-else-if="sortOrder === 1"
              class="cursor-pointer pi pi-sort-amount-up"
            ></div>
            <div
              v-else-if="sortOrder === -1"
              class="cursor-pointer pi pi-sort-amount-down"
            ></div>
          </div>
        </template>

        <template #body="slotProps">
          <div class="long-text-column" style="width: 20rem">
            <span>{{ slotProps.data?.nombre }}</span>
            <span v-if="slotProps.data?.apellido1"
              >&nbsp;{{ slotProps.data?.apellido1 }}</span
            >
            <span v-if="slotProps.data?.apellido2"
              >&nbsp;{{ slotProps.data?.apellido2 }}</span
            >
          </div>
          <div class="long-text-column">
            <span>{{ slotProps.data?.email }}</span>
          </div>
        </template>
      </Column>
      <Column v-if="showAdminColumn" field="gestionaUsuarios">
        <template #header>
          <div
            @click="sortBy('gestionaUsuarios')"
            class="flex gap-2 align-items-center cursor-pointer"
          >
            <div>Administrador</div>
            <div
              v-if="sortField != 'gestionaUsuarios'"
              class="pi pi-sort-alt"
            ></div>
            <div
              v-else-if="sortOrder === 1"
              class="cursor-pointer pi pi-sort-amount-up"
            ></div>
            <div
              v-else-if="sortOrder === -1"
              class="cursor-pointer pi pi-sort-amount-down"
            ></div>
          </div>
        </template>

        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.gestionaUsuarios"
            :trueValue="true"
            :falseValue="false"
            @change="handleUpdateUser(slotProps.data, 'gestionaUsuarios')"
          />
        </template>
      </Column>
      <Column field="activo">
        <template #header>
          <div
            @click="sortBy('activo')"
            class="flex gap-2 align-items-center cursor-pointer"
          >
            <div>Activo</div>
            <div v-if="sortField != 'activo'" class="pi pi-sort-alt"></div>
            <div
              v-else-if="sortOrder === 1"
              class="cursor-pointer pi pi-sort-amount-up"
            ></div>
            <div
              v-else-if="sortOrder === -1"
              class="cursor-pointer pi pi-sort-amount-down"
            ></div>
          </div>
        </template>

        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.activo"
            :trueValue="true"
            :falseValue="false"
            @change="handleUpdateUser(slotProps.data, 'activo')"
          />
        </template>
      </Column>
      <Column v-if="showAdminColumn" field="actualizaArchivos">
        <template #header>
          <div
            @click="sortBy('actualizaArchivos')"
            class="flex gap-2 align-items-center cursor-pointer"
          >
            <div>Archivos</div>
            <div
              v-if="sortField != 'actualizaArchivos'"
              class="pi pi-sort-alt"
            ></div>
            <div
              v-else-if="sortOrder === 1"
              class="cursor-pointer pi pi-sort-amount-up"
            ></div>
            <div
              v-else-if="sortOrder === -1"
              class="cursor-pointer pi pi-sort-amount-down"
            ></div>
          </div>
        </template>

        <template #body="slotProps">
          <InputSwitch
            v-model="slotProps.data.actualizaArchivos"
            :trueValue="true"
            :falseValue="false"
            @change="handleUpdateUser(slotProps.data, 'actualizaArchivos')"
          />
        </template>
      </Column>
      <Column v-if="tieneRoles" field="role" header="Rol">
        <template #body="slotProps">
          <MultiSelect
            v-model="slotProps.data.rolesDelInforme"
            :options="Roles"
            optionValue="idRolInforme"
            optionLabel="nombre"
            placeholder="Selecciona un rol"
            class="rol-multiselect"
            filter
            @change="handleUpdateUser(slotProps.data, 'Roles')"
            :showToggleAll="
              !rolesSelectorLoading.includes(slotProps.data.email)
            "
            :loading="rolesSelectorLoading.includes(slotProps.data.email)"
            :disabled="rolesSelectorLoading.includes(slotProps.data.email)"
          />
        </template>
      </Column>
      <Column field="grupos" header="Grupos">
        <template #body="slotProps">
          <div
            class="text-blue-200 cursor-pointer text-primary font-bold"
            @click="showUserHasGroupsModal(slotProps.data)"
          >
            <span v-if="slotProps.data.gruposDelUsuario?.length > 0">{{
              slotProps.data.gruposDelUsuario?.length
            }}</span>
            <span v-else>0</span>
          </div>
        </template>
      </Column>
      <Column field="ultimaVisita">
        <template #header>
          <div
            @click="sortBy('ultimaVisita')"
            class="flex gap-2 align-items-center cursor-pointer"
          >
            <div>Última visita</div>
            <div
              v-if="sortField != 'ultimaVisita'"
              class="pi pi-sort-alt"
            ></div>
            <div
              v-else-if="sortOrder === 1"
              class="cursor-pointer pi pi-sort-amount-up"
            ></div>
            <div
              v-else-if="sortOrder === -1"
              class="cursor-pointer pi pi-sort-amount-down"
            ></div>
          </div>
        </template>

        <template #body="slotProps">
          <div v-if="slotProps.data.ultimaVisita" class="white-space-nowrap">
            {{ formatDate(convertUtcToLocalTime(slotProps.data.ultimaVisita)) }}
            {{ formatTime(convertUtcToLocalTime(slotProps.data.ultimaVisita)) }}
          </div>
          <div v-else class="text-gray-400">Sin visita</div>
        </template>
      </Column>
      <Column>
        <template #body="slotProps">
          <Button
            @click="deleteUser(slotProps.data.email)"
            :icon="
              deletingUserEmail == slotProps.data.email
                ? 'pi pi-spin pi-spinner'
                : 'pi pi-trash'
            "
            severity="danger"
          />
        </template>
      </Column>
    </DataTable>
  </div>
  <Paginator
    :rows="rows"
    :totalRecords="totalResults"
    :rowsPerPageOptions="rowsPerPageOptions"
    @page="onPage"
  ></Paginator>

  <!-- User has groups modal -->
  <UserHasGroupsDialog
    ref="userHasGroupsModalRef"
    :user="selectedUser"
    @add-group="onPage()"
    @delete-group="onPage()"
  />
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import Button from "primevue/button";
import Menu from "primevue/menu";
import DataTable from "primevue/datatable";
import Paginator from "primevue/paginator";
import Column from "primevue/column";
import InputSwitch from "primevue/inputswitch";
import MultiSelect from "primevue/multiselect";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useAuthorization } from "@sttedra_ui/framework";

import FindUser from "@/components/partials/FindUser.vue";
import UserHasGroupsDialog from "@/pages/_root/user-has-groups-dialog/UserHasGroupsDialog.vue";
import {
  userReportAddUser,
  userReportUpdateUser,
  userReportDeleteUser,
  userReportDetailsUser,
} from "@/api/reports/reports.api";
import {
  formatDate,
  formatTime,
  convertUtcToLocalTime,
} from "@/composables/_commons/helper.js";
import { tipoClonacion } from "@/composables/_commons/enums.js";

// #region Props, Emits
const props = defineProps({
  informe: {
    type: Object,
    required: true,
  },
  idInforme: {
    type: String,
    required: true,
  },
  tieneRoles: {
    type: Boolean,
    required: true,
  },
  Roles: {
    type: Array,
    required: true,
  },
});

const emits = defineEmits(["copy"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const userHasGroupsModalRef = ref(null);
const rolesSelectorLoading = ref([]);
const loading = ref(false);
const deletingUserEmail = ref(null);
const findUserRef = ref(null);
const toast = useToast();
const usersArray = ref([]);
const confirm = useConfirm();
const { modelRef: authorizationModelRef } = useAuthorization();
const userRol = authorizationModelRef.value.roles[0];
const selectedImpersonation = ref(null);
const addUser = ref(null);
const search = ref("");
const sortField = ref(null);
const sortOrder = ref(null);
const pageIndex = ref(1);
const pageSize = ref(10);
const totalHeight = ref("0px");
const selectedUser = ref(null);

const rows = ref(10);
const rowsPerPageOptions = ref([10, 20, 50]);
const totalResults = ref(0);

// Menu ref and items
const menu = ref(null);
const menuItems = ref([
  {
    label: 'Importar usuarios de otro informe',
    icon: 'pi pi-download',
    command: () => {
      openCopyUsersAndGroupsModal(tipoClonacion.Users);
    }
  }
]);

const canManageUsers = computed(() => {
  return (
    props.informe?.gestionaUsuarios ||
    (selectedImpersonation.value
      ? selectedImpersonation.value.esAdministrador
      : userRol === "Admin")
  );
});

const showAdminColumn = computed(() => {
  let show = false;

  if (userRol == "Admin") {
    show = true;
  }

  if (selectedImpersonation.value) {
    show = selectedImpersonation.value.esAdministrador;
  }

  return show;
});
// #endregion

// #region Functions
function toggleMenu(event) {
  menu.value.toggle(event);
}

async function handleAddUser(user) {
  const userAlreadyAdded = usersArray.value.some(
    (u) => u.email.toLowerCase() === user.email.toLowerCase(),
  );

  if (userAlreadyAdded) {
    toast.add({
      severity: "error",
      summary: "Usuario ya añadido",
      detail: "El usuario ya ha sido añadido anteriormente",
      life: 3000,
    });
    return;
  }

  findUserRef.value.loading = true;

  addUser.value = {
    idInforme: props.idInforme,
    nombre: user.nombre,
    email: user.email,
    // actualizaArchivos: false,
    gestionaUsuarios: false,
    activo: true,
    habilitado: true,
    Roles: [],
  };

  const response = await userReportAddUser(addUser.value);
  const error = response.response?.data?.errors?.[0]?.message;

  if (!error) {
    await onPage();

    toast.add({
      severity: "success",
      summary: "Usuario añadido",
      detail: "El usuario ha sido añadido correctamente",
      life: 3000,
    });
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error,
      life: 3000,
    });
  }

  findUserRef.value.loading = false;

  setTimeout(() => {
    addUser.value = null;
  }, 2000);
}

async function handleUpdateUser(user, field) {
  rolesSelectorLoading.value.push(user.email);

  const response = await userReportUpdateUser({
    idInforme: props.idInforme,
    email: user.email,
    actualizaArchivos: user.actualizaArchivos,
    gestionaUsuarios: user.gestionaUsuarios,
    activo: user.activo,
    habilitado: user.activo,
    roles: user.rolesDelInforme,
  });

  rolesSelectorLoading.value = rolesSelectorLoading.value.filter(
    (email) => email != user.email,
  );

  if (response.errors) {
    if (field === "Roles") {
      user[field] = [];
    } else {
      user[field] = !user[field];
    }
  }
}

function deleteUser(email) {
  confirm.require({
    message: "¿Desea borrar el registro?",
    header: "Borrar",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      deletingUserEmail.value = email;

      const response = await userReportDeleteUser({
        idInforme: props.idInforme,
        email,
      });

      const error = response.response?.data?.errors?.[0]?.message;

      if (response == true || response == "") {
        onPage();

        toast.add({
          severity: "success",
          summary: "Usuario eliminado",
          detail: "El usuario ha sido eliminado correctamente",
          life: 3000,
        });
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error,
          life: 3000,
        });
      }

      deletingUserEmail.value = null;
    },
  });
}

function handleSearchUsers(query) {
  search.value = query;
  onPage();
}

function rowClass(data) {
  return [{ "blink-row": addUser.value && data.email == addUser.value.email }];
}

function sortBy(field) {
  sortField.value = field;

  switch (sortOrder.value) {
    case null:
      sortOrder.value = 1;
      break;

    case 1:
      sortOrder.value = -1;
      break;

    case -1:
      sortOrder.value = null;
      sortField.value = null;
      break;
  }

  onPage();
}

async function onPage(event = null) {
  loading.value = true;

  if (event) {
    rows.value = event.rows;
    pageIndex.value = event.page + 1;
    pageSize.value = event.rows;
  }

  let sortOrderFormatted = null;

  switch (sortOrder.value) {
    case 1:
      sortOrderFormatted = "asc";
      break;
    case -1:
      sortOrderFormatted = "desc";
      break;
    default:
      sortOrderFormatted = null;
  }

  const access = await userReportDetailsUser({
    alias: props.informe.alias,
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    sortField: sortField.value,
    sortOrder: sortOrderFormatted,
    GLOBALSEARCH: {
      VALUE: search.value,
      FIELDS: ["Titulo"],
    },
  });

  usersArray.value = access.data;
  totalResults.value = access.totalResults;

  loading.value = false;
}

async function setTableHeight() {
  const reportHasUsersDialog = document.querySelector(
    ".report-has-users-dialog",
  );

  if (reportHasUsersDialog) {
    await nextTick();

    const dialogHeader = reportHasUsersDialog.querySelector(".p-dialog-header");
    const tabViewNav = reportHasUsersDialog.querySelector(
      ".p-tabview-nav-container",
    );
    const datatableTHead =
      reportHasUsersDialog.querySelector(".p-datatable-thead");
    const paginatorWrapper = reportHasUsersDialog.querySelector(".p-paginator");
    const inputGroup = reportHasUsersDialog.querySelector(".p-inputgroup");

    const reportHasUsersDialogHeight = reportHasUsersDialog
      ? reportHasUsersDialog.clientHeight
      : 0;
    const dialogHeaderHeight = dialogHeader ? dialogHeader.clientHeight : 0;
    const tabViewNavHeight = tabViewNav ? tabViewNav.clientHeight : 0;
    const datatableTHeadHeight = datatableTHead
      ? datatableTHead.clientHeight
      : 0;
    const paginatorWrapperHeight = paginatorWrapper
      ? paginatorWrapper.clientHeight
      : 0;
    const inputGroupHeight = inputGroup ? inputGroup.clientHeight : 0;

    const maxTableHeight =
      reportHasUsersDialogHeight -
      dialogHeaderHeight -
      tabViewNavHeight -
      datatableTHeadHeight -
      paginatorWrapperHeight -
      inputGroupHeight;

    totalHeight.value = `calc(${maxTableHeight}px - 5px - 1.25rem  * 2 - 1rem)`;
  }
}

// Open the user has groups modal with the data
function showUserHasGroupsModal(data) {
  selectedUser.value = data;
  userHasGroupsModalRef.value.showModal();
}

function refreshData() {
  onPage();
}

function openCopyUsersAndGroupsModal(type) {
  emits("copy", type);
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  onPage();
  setTableHeight();
  window.addEventListener("resize", setTableHeight);

  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }
});

defineExpose({
  refreshData
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  :deep(.p-datatable-wrapper) {
    overflow: visible !important;
  }
}
</style>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.rol-multiselect {
  width: 15rem;

  @media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
    width: 60vw;
  }
}
</style>
